import React, { useEffect, useRef, useState } from 'react';

import { animated, useSpring } from 'react-spring';
import { useWalkthroughActive } from 'src/editor';
import {
  getIsSuggestionsAlertVisible,
  useEditorSuggestionState,
} from 'src/editor/editor-suggestion-state-registry';
import { editorSuggestionsAlertId } from 'src/editor/EditorAlert/editor-alert-ids';
import { useEditorViewPreferences } from 'src/editor/vars';
import { useAlineMeasure } from 'src/hooks/dom-hooks';
import { useEditingMode } from 'src/store/document/selectors';
import { makeStyles } from 'tss-react/mui';

const OUTLINE_BASE_TOP_PX = 16;

interface StyleProps {
  topPx: number;
  verticalRulerDisabled: boolean;
}

const useAnimationStyles = makeStyles<StyleProps>()((theme, { topPx, verticalRulerDisabled }) => ({
  animationContent: {
    height: '100%',
  },
  animationRoot: {
    display: 'flex',
    flexDirection: 'column',
    left: verticalRulerDisabled ? 0 : '16px',
    maxHeight: 'calc(100% - 16px)',
    minHeight: '100px',
    overflowX: 'hidden',
    paddingLeft: '10px',
    paddingRight: '12px',
    position: 'absolute',
    top: topPx,
    transition: 'top 200ms cubic-bezier(0.4, 0, 0.2, 1)',
    zIndex: theme.zIndex.drawer,
  },
  hiddenScrollbar: {
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    scrollbarWidth: 'none',
  },
}));

interface OutlineViewAnimationContainerProps {
  animationWidth: number;
  children: React.ReactNode;
  isOpen: boolean;
}

export const OutlineAnimationContainer: React.FC<OutlineViewAnimationContainerProps> = ({
  animationWidth,
  children,
  isOpen,
}) => {
  const { walkThroughActive } = useWalkthroughActive();
  const { showRulers } = useEditorViewPreferences();
  const { commenting, viewing } = useEditingMode();
  const suggestionState = useEditorSuggestionState();
  const isSuggestionsAlertVisible = getIsSuggestionsAlertVisible(suggestionState);
  const suggestionsAlertHeight = useSuggestionsAlertHeight(isSuggestionsAlertVisible);
  const { classes } = useAnimationStyles({
    topPx: OUTLINE_BASE_TOP_PX + suggestionsAlertHeight,
    verticalRulerDisabled: walkThroughActive || !showRulers || viewing || commenting,
  });

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const container = containerRef.current;

    let timer: ReturnType<typeof setTimeout>;

    if (isOpen) {
      container.style.display = 'block';
      timer = setTimeout(() => {
        container.classList.remove(classes.hiddenScrollbar);
      }, 200);
    } else {
      container.classList.add(classes.hiddenScrollbar);
      timer = setTimeout(() => {
        container.style.display = 'none';
      }, 300);
    }

    return () => clearTimeout(timer);
  }, [classes.hiddenScrollbar, isOpen]);

  const containerStyles = useSpring({
    width: animationWidth,
  });

  const contentStyles = useSpring({
    marginLeft: isOpen ? 0 : -animationWidth,
    marginRight: isOpen ? 0 : animationWidth,
    opacity: isOpen ? 1 : 0,
  });

  return (
    <animated.div ref={containerRef} className={classes.animationRoot} style={containerStyles}>
      <animated.div className={classes.animationContent} style={contentStyles}>
        {children}
      </animated.div>
    </animated.div>
  );
};

function useSuggestionsAlertHeight(isSuggestionsAlertVisible: boolean) {
  const [alertElement, setAlertElement] = useState<HTMLElement | null>(null);
  const [, measurements] = useAlineMeasure(alertElement);

  useEffect(() => {
    const animationFrameId = window.requestAnimationFrame(() => {
      if (!isSuggestionsAlertVisible) {
        setAlertElement(null);
        return;
      }

      const alert = document.getElementById(editorSuggestionsAlertId);
      setAlertElement(alert instanceof HTMLElement ? alert : null);
    });

    return () => window.cancelAnimationFrame(animationFrameId);
  }, [isSuggestionsAlertVisible]);

  return isSuggestionsAlertVisible ? (measurements?.height ?? 0) : 0;
}
